import { Dispatch, SetStateAction, useState } from 'react';
import { Level } from '../Modal/type';
import { Modal } from '../Modal/Modal';
import {
  ActionsWrapper,
  ButtonContainer,
  Container,
  DiscountsTypeWrapper,
  DiscountTypeOption,
  InputWrapper,
  Reset,
  ResetText,
  Title,
  TitleContainer,
} from './EditDiscountModal.styled';
import Button from 'components/Button/Button';
import { useBreakpointFlag } from 'utils/hooks/useBreakpointFlag';
import { useTranslation } from 'react-i18next';
import Input from 'components/Input/Input';
import { ArrowCounterClockwise, Percent } from '@phosphor-icons/react';
import { useCurrencySymbol } from 'utils/hooks/useFormatMoney';
import { validateDiscountValue } from './helpers';
import { Checkbox } from 'components/Checkbox/Checkbox';
import Icon from 'components/Icon/Icon';
import { COLORS } from 'assets/styled';
import { ConfirmModal } from '../ConfirmModal/ConfirmModal';
import { useSelector } from 'react-redux';
import { IRootReducerState } from 'store/store';

interface EditDiscountModalProps {
  isOpen: boolean;
  setIsOpen: Dispatch<SetStateAction<boolean>>;
  onConfirm: (discountValue: string, discountType: DiscountsType) => void;
  onReset?: (applyToAll: boolean) => void;
  level?: Level;
  disabled?: boolean;
  defaultValue?: string | null;
  defaultType?: DiscountsType | null;
  setApplyToAll: Dispatch<SetStateAction<boolean>>;
  applyToAll: boolean;
  grossPrice: string | null;
}

export enum DiscountsType {
  PERCENTAGE = 'PERCENTAGE',
  AMOUNT = 'AMOUNT',
}

const EditDiscountModal = ({
  isOpen,
  setIsOpen,
  onConfirm,
  onReset,
  level = 'FIRST',
  disabled,
  defaultValue,
  defaultType,
  setApplyToAll,
  applyToAll,
  grossPrice,
}: EditDiscountModalProps) => {
  const { t } = useTranslation();
  const { isSmallPhone, isPhone } = useBreakpointFlag();
  const [isConfirmModalOpen, setIsConfirmModalOpen] = useState<boolean>(false);
  const [discount, setDiscount] = useState<string>(defaultValue ?? '');
  const [discountType, setDiscountType] = useState<DiscountsType>(
    defaultType ?? DiscountsType.PERCENTAGE
  );

  const { currency } = useSelector(
    (state: IRootReducerState) => state.shoppingCartInfo
  );

  const currencySymbol = useCurrencySymbol(currency);

  const errorMessage = validateDiscountValue(
    discount,
    discountType,
    t,
    grossPrice
  );

  return (
    <div onMouseDown={(e: any) => e.stopPropagation()}>
      <Modal
        isOpen={isOpen}
        onClose={() => {
          setIsOpen(false);
        }}
        modalStyle={{
          margin: 'auto',
          width: isPhone ? '95%' : '600rem',
          minWidth: isPhone ? '95%' : 'auto',
          position: 'fixed',
        }}
        level={level}
      >
        <Container>
          <TitleContainer>
            <Title>{t('Edit discount')}</Title>
          </TitleContainer>

          <DiscountsTypeWrapper>
            <DiscountTypeOption
              $activeDiscountOption={discountType === DiscountsType.PERCENTAGE}
              onClick={() => {
                setDiscountType(DiscountsType.PERCENTAGE);
                setApplyToAll(false);
                setDiscount('');
              }}
            >
              {t('Percentage')}
            </DiscountTypeOption>
            <DiscountTypeOption
              $activeDiscountOption={discountType === DiscountsType.AMOUNT}
              onClick={() => {
                setDiscountType(DiscountsType.AMOUNT);
                setApplyToAll(false);
                setDiscount('');
              }}
            >
              {t('Net Amount')}
            </DiscountTypeOption>
          </DiscountsTypeWrapper>

          <InputWrapper>
            <Input
              label=""
              placeholder=""
              value={discount}
              errorMessage={errorMessage}
              width={'100%'}
              icon={
                discountType === DiscountsType.PERCENTAGE ? Percent : undefined
              }
              symbol={
                discountType === DiscountsType.AMOUNT
                  ? currencySymbol
                  : undefined
              }
              hideNumberArrows
              type="text"
              wrapperStyles={{ width: '100%', maxWidth: '300rem' }}
              onChange={(e) => {
                const rawValue = e.target.value;
                const normalizedValue = rawValue.replace(',', '.');

                if (
                  discountType === DiscountsType.PERCENTAGE &&
                  normalizedValue.includes('-')
                ) {
                  return;
                }

                const regex = /^-?\d*\.?\d*$/;

                if (normalizedValue === '' || regex.test(normalizedValue)) {
                  setDiscount(normalizedValue);
                }
              }}
              onBlur={() => {
                const normalized = discount.replace(',', '.');
                const number = parseFloat(normalized);

                if (!discount || discount === '-' || isNaN(number)) {
                  setDiscount('');
                  return;
                }

                const clampedValue =
                  discountType === DiscountsType.PERCENTAGE
                    ? Math.min(Math.max(number, 0), 100)
                    : number;

                setDiscount(clampedValue.toFixed(2));
              }}
            />
          </InputWrapper>

          <ActionsWrapper>
            {discountType === DiscountsType.PERCENTAGE && (
              <Checkbox
                isChecked={applyToAll}
                label={t('Apply to all lines')}
                onChange={() => setApplyToAll(!applyToAll)}
              />
            )}
            <Reset onClick={() => setIsConfirmModalOpen(true)}>
              <Icon
                svg={ArrowCounterClockwise}
                color={COLORS.RED_100}
                size={20}
                weight="regular"
              />
              <ResetText>{t('Reset discount')}</ResetText>
            </Reset>
          </ActionsWrapper>

          <ButtonContainer>
            <Button
              disabled={disabled}
              onClick={() => setIsOpen(false)}
              secondary
              width={isSmallPhone ? '150rem' : '200rem'}
              label={t('Cancel')}
            />
            <Button
              disabled={!discount || !!errorMessage}
              onClick={() => onConfirm(discount, discountType)}
              primary
              width={isSmallPhone ? '150rem' : '200rem'}
              label={t('Confirm')}
            />
          </ButtonContainer>
        </Container>

        <ConfirmModal
          isOpen={isConfirmModalOpen}
          setIsOpen={setIsConfirmModalOpen}
          title={t('Reset applied discount')}
          description={
            applyToAll
              ? t('Are you sure you want to reset all applied discount?')
              : t(
                  'Are you sure you want to reset the applied discount for this line?'
                )
          }
          onConfirm={() => {
            onReset?.(applyToAll);
            setIsConfirmModalOpen(false);
          }}
          onCancel={() => setIsConfirmModalOpen(false)}
          level="SECOND"
        />
      </Modal>
    </div>
  );
};

export default EditDiscountModal;
